import { Injectable } from '@angular/core';

export interface CartSessionEnvelope {
  version: 1;
  items: any[];
  clientOrderKey: string | null;
  /**
   * Identity of the current customer's payment session.
   *
   * Unlike clientOrderKey, this key survives cart mutations. It prevents a
   * genuinely unknown myPOS outcome from being bypassed by adding/removing an
   * item, while a cleared cart still starts a completely independent session
   * for the next customer (even for identical products and amount).
   */
  paymentSessionKey: string | null;
}

/**
 * One sessionStorage record owns the cart, its backend idempotency key, and
 * the independent payment-session key. A single synchronous setItem keeps
 * those three values mutually consistent across WebView reloads.
 */
@Injectable({ providedIn: 'root' })
export class CartSessionEnvelopeService {
  private readonly storageKey = 'cart';

  load(): CartSessionEnvelope {
    const raw = sessionStorage.getItem(this.storageKey);
    if (raw === null) {
      return {
        version: 1,
        items: [],
        clientOrderKey: null,
        paymentSessionKey: null,
      };
    }
    const parsed = JSON.parse(raw);
    // Stage-v2 compatibility: the historical value was the items array.
    if (Array.isArray(parsed)) {
      return {
        version: 1,
        items: this.clone(parsed),
        clientOrderKey: null,
        paymentSessionKey: null,
      };
    }
    const paymentSessionKey = parsed?.paymentSessionKey ?? null;
    if (
      !parsed ||
      parsed.version !== 1 ||
      !Array.isArray(parsed.items) ||
      !(
        parsed.clientOrderKey === null ||
        (
          typeof parsed.clientOrderKey === 'string' &&
          parsed.clientOrderKey.startsWith('idmp-')
        )
      ) ||
      !(
        paymentSessionKey === null ||
        (
          typeof paymentSessionKey === 'string' &&
          paymentSessionKey.startsWith('idmp-')
        )
      )
    ) {
      throw new Error('INVALID_CART_SESSION_ENVELOPE');
    }
    return this.clone({
      version: 1,
      items: parsed.items,
      clientOrderKey: parsed.clientOrderKey,
      paymentSessionKey,
    });
  }

  write(
    items: any[],
    clientOrderKey: string | null,
    paymentSessionKey?: string | null,
  ): void {
    if (!Array.isArray(items)) {
      throw new Error('INVALID_CART_SESSION_ITEMS');
    }
    if (
      clientOrderKey !== null &&
      (
        typeof clientOrderKey !== 'string' ||
        !clientOrderKey.startsWith('idmp-')
      )
    ) {
      throw new Error('INVALID_CART_SESSION_ORDER_KEY');
    }
    // Existing callers intentionally omit the third argument when a cart
    // mutation rotates only the backend order UUID. Preserve the independent
    // payment-session identity in that case.
    const resolvedPaymentSessionKey =
      paymentSessionKey === undefined
        ? this.load().paymentSessionKey
        : paymentSessionKey;
    if (
      resolvedPaymentSessionKey !== null &&
      (
        typeof resolvedPaymentSessionKey !== 'string' ||
        !resolvedPaymentSessionKey.startsWith('idmp-')
      )
    ) {
      throw new Error('INVALID_CART_SESSION_PAYMENT_KEY');
    }
    const envelope: CartSessionEnvelope = {
      version: 1,
      items: this.clone(items),
      clientOrderKey,
      paymentSessionKey: resolvedPaymentSessionKey,
    };
    sessionStorage.setItem(this.storageKey, JSON.stringify(envelope));
  }

  clear(): void {
    sessionStorage.removeItem(this.storageKey);
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }
}
